import React from "react";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Business,
  Logout,
  SettingsOutlined,
  ShareOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { useAuth } from "../../../auth/AuthProvider";
import { useHeader } from "../../../contexts/HeaderContext";
import { colors } from "../../../styles/theme";
import ZugriffDialog from "./zugriff/ZugriffDialog";

const ADMIN_PERMISSION = "admin:write";
const ORGANISATION_PERMISSION = "organisation:write";

/**
 * Einstellungsmenü rechts im Header: ein Zahnrad, beim Klick die Account-Aktionen.
 *
 * Oben steht, wer angemeldet ist. „Projekt teilen“ erscheint nur in einem Projekt, und nur,
 * wenn das Projekt-Layout es anbietet (Mitglied des Mandanten, `useHeaderShareProject`) und der
 * Nutzer Freigaben ändern darf (`organisation:write`) – sonst führte der Eintrag in einen
 * Dialog, in dem er nichts tun kann.
 */
const ProfileMenu: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { shareProject } = useHeader();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [shareOpen, setShareOpen] = React.useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShareProject = () => {
    handleClose();
    setShareOpen(true);
  };

  const handleManageOrganisation = () => {
    handleClose();
    if (user?.organisation_id) {
      navigate(`/admin/organisations/${user.organisation_id}`);
    }
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  if (!isAuthenticated) {
    return null;
  }

  const canManageOrganisation = Boolean(
    user?.organisation_id &&
    (user?.permissions.includes(ADMIN_PERMISSION) ||
      user?.permissions.includes(ORGANISATION_PERMISSION)),
  );
  const canShareProject = Boolean(
    shareProject && user?.permissions.includes(ORGANISATION_PERMISSION),
  );

  const userName =
    user?.display_name ||
    (user?.first_name &&
      user?.last_name &&
      `${user.first_name} ${user.last_name}`) ||
    user?.username;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Einstellungen"
        aria-haspopup="menu"
        sx={{ color: colors.text.secondary }}
      >
        <SettingsOutlined />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
          <ListItemText
            primary={userName}
            secondary={user?.email}
            slotProps={{ primary: { sx: { fontWeight: 500 } } }}
          />
        </MenuItem>
        <Divider />
        {canShareProject && (
          <MenuItem onClick={handleShareProject}>
            <ListItemIcon>
              <ShareOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Projekt teilen</ListItemText>
          </MenuItem>
        )}
        {canManageOrganisation && (
          <MenuItem onClick={handleManageOrganisation}>
            <ListItemIcon>
              <Business fontSize="small" />
            </ListItemIcon>
            <ListItemText>Organisation verwalten</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: colors.status.error.main }} />
          </ListItemIcon>
          <ListItemText>Abmelden</ListItemText>
        </MenuItem>
      </Menu>

      {shareProject && (
        <ZugriffDialog
          projectId={shareProject.id}
          projectName={shareProject.name}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>
  );
};

export default ProfileMenu;
